import type { z } from 'zod';
import {
  type AxiosInstance,
  isAxiosError,
  type Method,
  type ResponseType,
} from 'axios';

import { logger } from '@/utils/logger';
import axiosInstance from '@/components/lib/axios';
import { handleAxiosError } from './error.helper';

// Renamed to follow modern TypeScript conventions (no 'T' prefix)
type ApiCallPayload<Request, Response> = {
  path: string;
  method: Method;
  requestSchema: z.ZodType<Request>;
  responseSchema: z.ZodType<Response>;
  headers?: Record<string, string>;
  axios?: AxiosInstance;
  isMultipart?: boolean;
  responseType?: ResponseType;
};

/**
 * A generic, type-safe API client function creator.
 * @returns An async function that takes request data, validates it,
 * makes the API call, and validates the response.
 */
export function api<Request, Response>({
  path,
  method,
  requestSchema,
  responseSchema,
  headers = {},
  axios = axiosInstance,
  isMultipart = false,
  responseType,
}: ApiCallPayload<Request, Response>) {
  return async (requestData: Request): Promise<Response> => {
    // 1. ✅ Validate the request data before sending. Throws if invalid.
    const parsedData = requestSchema.parse(requestData);

    try {
      // 2. ✅ Make the API call.
      const response = await axios({
        method,
        url: path,
        params:
          method === 'GET' || method === 'DELETE' ? parsedData : undefined,
        data:
          method === 'POST' || method === 'PUT' || method === 'PATCH'
            ? parsedData
            : undefined,
        headers: isMultipart
          ? { 'Content-Type': 'multipart/form-data', ...headers }
          : { 'Content-Type': 'application/json', ...headers },
        responseType,
      });

      // 3. ✅ Validate the response data.
      const result = responseSchema.safeParse(response.data);

      if (result.success) {
        return result.data;
      }

      logger.error('Response validation failed at', path, result.error.message);
      throw result.error;
    } catch (error) {
      if (isAxiosError(error)) {
        throw handleAxiosError(error);
      }

      const normalizedError =
        error instanceof Error ? error : new Error('Unknown error');
      logger.error('API call failed at', path, normalizedError.message);
      throw normalizedError;
    }
  };
}
